import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, CheckCircle2, Loader2, Rocket } from 'lucide-react';
import { activerProduction } from '../services/api';

/** Ce que l'administrateur doit recopier pour armer le bouton. */
const CONFIRMATION = 'PRODUCTION';

/**
 * Passage du mode essai au mode production.
 *
 * L'action supprime les données financières d'essai — devis, factures, dons,
 * réservations — et remet la numérotation à zéro. Elle est irréversible pour
 * l'administrateur : seul le support de la plateforme peut repasser en essai,
 * et ce retour ne rétablit rien.
 *
 * Trois verrous avant d'agir, parce qu'aucun seul ne suffit : la liste
 * explicite de ce qui va disparaître, un mot à recopier — un clic de trop ne
 * tape pas « PRODUCTION » —, et le mot de passe, vérifié par le serveur.
 */
export default function MiseEnProduction() {
  const queryClient = useQueryClient();
  const [confirmation, setConfirmation] = useState('');
  const [password, setPassword] = useState('');
  const [resultat, setResultat] = useState<{ ok: boolean; message: string } | null>(null);

  const activer = useMutation({
    mutationFn: () => activerProduction(password),
    onSuccess: (d: any) => {
      setResultat({ ok: true, message: d.message });
      setPassword('');
      setConfirmation('');
      // Le ruban DEMO lit la personnalisation : il doit tomber tout de suite.
      queryClient.invalidateQueries({ queryKey: ['personnalisation'] });
      queryClient.invalidateQueries({ queryKey: ['admin-parametres'] });
    },
    onError: (e: Error) => setResultat({ ok: false, message: e.message }),
  });

  const armee = confirmation.trim().toUpperCase() === CONFIRMATION && password.length > 0;

  return (
    <div className="space-y-4">
      <div className="rounded-xl border-2 border-red-200 bg-red-50 dark:bg-red-900/20 dark:border-red-800 p-4 space-y-2 text-sm text-red-800 dark:text-red-200">
        <p className="font-semibold flex items-center gap-2">
          <AlertTriangle className="w-4 h-4 shrink-0" />
          Le passage en production supprime définitivement :
        </p>
        <ul className="list-disc list-inside space-y-0.5">
          <li>tous les devis, factures et avoirs d'essai, avec leurs PDF ;</li>
          <li>toutes les souscriptions de sponsors et leurs visuels déposés ;</li>
          <li>tous les dons et toutes les réservations de repas et de concert ;</li>
          <li>les exercices d'entraînement.</li>
        </ul>
        <p>
          La numérotation des devis, factures et avoirs repart de 0001 : la première
          facture réelle ouvre la séquence légale. Les fiches des membres, des sponsors
          et des événements ne sont pas touchées — supprimez les fictives une à une
          depuis leurs écrans.
        </p>
        <p className="font-medium">
          Ce passage est irréversible : seul le support de la plateforme peut repasser le
          site en essai, et ce retour ne restaure rien.
        </p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <label className="text-sm text-gray-600 dark:text-gray-400 block">
          Recopiez « {CONFIRMATION} » pour confirmer
          <input
            className="input mt-1"
            value={confirmation}
            onChange={(e) => setConfirmation(e.target.value)}
            placeholder={CONFIRMATION}
            autoComplete="off"
          />
        </label>
        <label className="text-sm text-gray-600 dark:text-gray-400 block">
          Votre mot de passe
          <input
            className="input mt-1"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoComplete="current-password"
          />
        </label>
      </div>

      <button
        type="button"
        onClick={() => {
          setResultat(null);
          activer.mutate();
        }}
        disabled={!armee || activer.isPending}
        className="btn-primary inline-flex items-center gap-2 !bg-red-600 hover:!bg-red-700 disabled:opacity-50"
      >
        {activer.isPending ? (
          <Loader2 className="w-4 h-4 animate-spin" />
        ) : (
          <Rocket className="w-4 h-4" />
        )}
        Passer le site en production
      </button>

      {resultat && (
        <p
          className={`text-sm rounded-xl px-3 py-2 flex items-start gap-2 ${
            resultat.ok
              ? 'bg-green-50 text-green-800 dark:bg-green-900/30 dark:text-green-200'
              : 'bg-red-50 text-red-700'
          }`}
        >
          {resultat.ok ? (
            <CheckCircle2 className="w-4 h-4 mt-0.5 shrink-0" />
          ) : (
            <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
          )}
          {resultat.message}
        </p>
      )}
    </div>
  );
}
